import React from 'react';
import './Insights.css';
import { AspectForecast, InsightsLiteDto, ProfileHighlightSummary, ProfileSummaries, ProfileSummary } from '../api';
import { useTranslation } from 'react-i18next';
import useAstroApiClient from '../hooks/useAstroApiClient';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { AlertTriangle, Calendar, Moon, Star, Sun, TrendingUp } from 'lucide-react';
import { Progress } from './ui/progress';
import { getScoreTone } from '../helpers/score-tone';
import ErrorRetryCard from './ErrorRetryCard';
import InsightsCalendar from './insights/InsightsCalendar';
import InsightsLoadingSkeleton from './insights/InsightsLoadingSkeleton';
import ProfileScoreCard from './insights/ProfileScoreCard';
import ScoreHeader from './insights/ScoreHeader';
import WeeklyStrip from './insights/WeeklyStrip';
import { deriveCalendarDays } from './insights/insightsCalendarUtils';

// Ordem de apresentacao dos cartoes diarios do Pro (mantida do layout original).
const DAILY_CARD_PROFILE_ORDER = ['Relationships', 'Career', 'Investment', 'Health', 'Spiritual', 'Travel'];

type DailyHighlightItem = {
  id: string;
  profileKey: string;
  profileScore: number;
  contribution: number;
  aspect: AspectForecast;
};

const DAILY_HIGHLIGHT_LIMIT = 5;
const DAILY_ATTENTION_LIMIT = 3;
const DAILY_MAX_PER_PROFILE = 2;
const DAILY_ENSURED_PROFILE_COUNT = 3;

const buildDailyAspectPool = (
  summaries: ProfileSummary[] | undefined,
  direction: 'positive' | 'negative',
): DailyHighlightItem[] => {
  const isPositive = direction === 'positive';

  return (summaries ?? [])
    .flatMap((summary, summaryIndex) =>
      (summary.transitEvaluation?.aspectForecasts ?? [])
        .filter(aspect => {
          const contribution = aspect?.contribution ?? 0;
          return isPositive ? contribution > 0 : contribution < 0;
        })
        .map((aspect, aspectIndex) => ({
          id: `${summary.profileKey ?? 'unknown'}-${summaryIndex}-${aspectIndex}`,
          profileKey: summary.profileKey ?? '',
          profileScore: summary.transitEvaluation?.percentAbsolute ?? 0,
          contribution: aspect?.contribution ?? 0,
          aspect,
        }))
    )
    .sort((a, b) => {
      if (isPositive) {
        return (b.contribution - a.contribution) || (b.profileScore - a.profileScore);
      }

      return (a.contribution - b.contribution) || (b.profileScore - a.profileScore);
    });
};

const selectDiversifiedHighlights = (
  pool: DailyHighlightItem[],
  priorityProfileKeys: string[],
  limit: number,
  maxPerProfile: number,
  ensuredProfiles: number,
): DailyHighlightItem[] => {
  const selected: DailyHighlightItem[] = [];
  const selectedIds = new Set<string>();
  const countsByProfile = new Map<string, number>();

  const addItem = (item: DailyHighlightItem) => {
    if (selectedIds.has(item.id)) {
      return false;
    }

    const currentCount = countsByProfile.get(item.profileKey) ?? 0;
    if (currentCount >= maxPerProfile) {
      return false;
    }

    selected.push(item);
    selectedIds.add(item.id);
    countsByProfile.set(item.profileKey, currentCount + 1);
    return true;
  };

  for (const profileKey of priorityProfileKeys.slice(0, ensuredProfiles)) {
    if (selected.length >= limit) {
      break;
    }

    const candidate = pool.find(item => item.profileKey === profileKey && !selectedIds.has(item.id));
    if (candidate) {
      addItem(candidate);
    }
  }

  for (const item of pool) {
    if (selected.length >= limit) {
      break;
    }

    addItem(item);
  }

  return selected;
};

const Insights: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [reports, setReports] = React.useState<ProfileSummaries | null>(null);
  const [weeklyHighlights, setWeeklyHighlights] = React.useState<ProfileHighlightSummary | null>(null);
  const [monthlyHighlights, setMonthlyHighlights] = React.useState<ProfileHighlightSummary | null>(null);
  // Base partilhada com a Lite: scores por dia e por perfil, para todos os dias.
  const [liteBase, setLiteBase] = React.useState<InsightsLiteDto | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);
  const api = useAstroApiClient();

  const formatDateOnly = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return [year, month, day].join('-');
  };

  // Dados primarios do dia. Deixa o erro propagar para que o load consiga
  // distinguir sucesso de falha e mostrar o card de erro (deixa de falhar em silencio).
  const fetchDaily = async () => {
    const keys = [
      'Investment',
      'Relationships',
      'Health',
      'Spiritual',
      'Career',
      'Travel',
    ];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const data = await api.apiAstrologyTransitReadingsMe(formatDateOnly(today), keys.join(','));
    setReports(data);
  };

  const fetchWeekly = async () => {
    try {
      const now = new Date();
      const day = now.getDay();
      const diffToMonday = (day + 6) % 7;
      const monday = new Date(now);
      monday.setHours(0, 0, 0, 0);
      monday.setDate(now.getDate() - diffToMonday);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      const profiles = ['Investment', 'Relationships', 'Health', 'Spiritual', 'Career', 'Travel'].join(',');
      const response = await api.apiAstrologyTransitHighlightsMe(formatDateOnly(monday), formatDateOnly(sunday), profiles);
      setWeeklyHighlights(response);
    } catch (e) {
      console.error(e);
      setWeeklyHighlights(null);
    }
  };

  const fetchMonthly = async () => {
    try {
      const now = new Date();

      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      monthStart.setHours(0, 0, 0, 0);

      const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      monthEnd.setHours(23, 59, 59, 999);

      const profiles = ['Investment', 'Relationships', 'Health', 'Spiritual', 'Career', 'Travel'].join(',');
      const response = await api.apiAstrologyTransitHighlightsMe(
        formatDateOnly(monthStart),
        formatDateOnly(monthEnd),
        profiles
      );

      setMonthlyHighlights(response);
    } catch (e) {
      console.error(e);
      setMonthlyHighlights(null);
    }
  };


  const fetchLiteBase = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const profiles = ['Investment', 'Relationships', 'Health', 'Spiritual', 'Career', 'Travel'].join(',');
      const response = await api.apiAstrologyInsightsLiteMe(formatDateOnly(today), profiles);
      setLiteBase(response ?? null);
    } catch (e) {
      console.error(e);
      setLiteBase(null);
    }
  };

  const loadInsights = React.useCallback(async () => {
    setLoading(true);
    setLoadError(false);

    try {
      await fetchDaily();
    } catch (err) {
      console.error(err);
      setReports(null);
      setLoadError(true);
      setLoading(false);
      return;
    }

    // Dados secundarios (semana, mes, base Lite) nao bloqueiam a vista principal.
    await Promise.allSettled([fetchWeekly(), fetchMonthly(), fetchLiteBase()]);
    setLoading(false);
  }, [api]);

  React.useEffect(() => {
    void loadInsights();
  }, [loadInsights]);

  const profileKeys = React.useMemo(
    () => ['Investment', 'Relationships', 'Health', 'Spiritual', 'Career', 'Travel'],
    []
  );

  const orderedDailyProfiles = React.useMemo(
    () => [...(reports?.summaries ?? [])]
      .sort((a, b) => (b.transitEvaluation?.percentAbsolute ?? 0) - (a.transitEvaluation?.percentAbsolute ?? 0)),
    [reports]
  );

  const topProfile = orderedDailyProfiles[0];
  const priorityProfileKeys = React.useMemo(
    () => orderedDailyProfiles
      .map(profile => profile.profileKey ?? '')
      .filter(Boolean),
    [orderedDailyProfiles]
  );

  const dailyHighlights = React.useMemo(() => {
    const pool = buildDailyAspectPool(orderedDailyProfiles, 'positive');

    return selectDiversifiedHighlights(
      pool,
      priorityProfileKeys,
      DAILY_HIGHLIGHT_LIMIT,
      DAILY_MAX_PER_PROFILE,
      DAILY_ENSURED_PROFILE_COUNT,
    );
  }, [orderedDailyProfiles, priorityProfileKeys]);

  const dailyAttention = React.useMemo(() => {
    const pool = buildDailyAspectPool(orderedDailyProfiles, 'negative');

    return selectDiversifiedHighlights(
      pool,
      priorityProfileKeys,
      DAILY_ATTENTION_LIMIT,
      1,
      2,
    );
  }, [orderedDailyProfiles, priorityProfileKeys]);

  const dailyScores = React.useMemo(() => {
    const map: Record<string, { score: number; forecast: string }> = {};
    for (const k of profileKeys) {
      const p = reports?.summaries?.find(x => x.profileKey === k)?.transitEvaluation;
      const score = Math.round((p?.percentAbsolute ?? 0) as number);
      map[k] = { score: isFinite(score) ? score : 0, forecast: p?.globalForecast ?? '...' };
    }
    return map;
  }, [reports, profileKeys]);

  const dailyAvg = React.useMemo(() => {
    const vals = profileKeys.map(k => dailyScores[k]?.score ?? 0);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
  }, [dailyScores, profileKeys]);


  const formatShortDate = (date: Date, language: string) =>
    date.toLocaleDateString(language, { day: '2-digit', month: 'short' });

  const weekRange = React.useMemo(() => {
    const now = new Date();
    const diffToMonday = (now.getDay() + 6) % 7;
    const monday = new Date(now);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(now.getDate() - diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { monday, sunday };
  }, []);

  const monthRange = React.useMemo(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { start, end };
  }, []);

  // Deriva a base de calendario (formato Lite) a partir dos highlights Pro,
  // para que o Pro renderize a mesma faixa semanal e grelha mensal da Lite.
  // Preferir a base completa da Lite (todos os dias, todos os perfis); recorrer
  // a derivacao dos highlights so quando a base nao estiver disponivel.
  const weekCalendarDays = React.useMemo(() => {
    const base = liteBase?.week?.calendar ?? [];
    return base.length > 0 ? base : deriveCalendarDays(weeklyHighlights, weekRange.monday, weekRange.sunday);
  }, [liteBase, weeklyHighlights, weekRange]);

  const monthCalendarDays = React.useMemo(() => {
    const base = liteBase?.month?.calendar ?? [];
    return base.length > 0 ? base : deriveCalendarDays(monthlyHighlights, monthRange.start, monthRange.end);
  }, [liteBase, monthlyHighlights, monthRange]);

  const monthCalendarStart = liteBase?.month?.startDate ?? monthRange.start;

  if (loading) {
    return <InsightsLoadingSkeleton ariaLabel={t('insightsLoading')} />;
  }

  if (loadError) {
    return (
      <ErrorRetryCard
        message={t('insightsLoadError')}
        retryLabel={t('insightsRetry')}
        onRetry={loadInsights}
      />
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <ScoreHeader
        title={t('insightsTitle')}
        subtitle={t('insightsSubtitle')}
        dateLabel={formatShortDate(new Date(), i18n.language)}
      />

      <Tabs defaultValue="daily" className="w-full">
        <TabsList className="grid h-auto w-full grid-cols-3 gap-1 border border-stone-200 bg-white p-1 shadow-sm">
          <TabsTrigger value="daily" className="w-full data-[state=active]:bg-action-100 text-xs md:text-sm whitespace-nowrap flex-shrink-0">
            {t('insightsTabToday')}
          </TabsTrigger>
          <TabsTrigger value="weekly" className="w-full data-[state=active]:bg-action-100 text-xs md:text-sm whitespace-nowrap flex-shrink-0">
            {t('insightsTabWeek')}
          </TabsTrigger>
          <TabsTrigger value="monthly" className="w-full data-[state=active]:bg-action-100 text-xs md:text-sm whitespace-nowrap flex-shrink-0">
            {t('insightsTabMonth')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="daily" className="space-y-4 md:space-y-6">
          <Card className="border-stone-200 bg-white shadow-sm transition-shadow hover:shadow-md">
            <CardHeader>
              <CardTitle className="text-slate-800 flex items-center">
                <Sun className="h-6 w-6 mr-2 text-yellow-400" />
                {t('insightsEnergyTitle')}
              </CardTitle>
              <CardDescription className="text-slate-600">{t('insightsEnergySubtitle')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center space-x-4">
                <div className="text-4xl font-bold text-slate-800">{dailyAvg}%</div>
                <div className="flex-1">
                  <Progress value={dailyAvg} className="h-3" indicatorClassName={getScoreTone(dailyAvg)} />
                  <p className="text-sm text-slate-600 mt-1">{t('insightsEnergyOverall')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {DAILY_CARD_PROFILE_ORDER.map((profileKey) => {
              const entry = dailyScores[profileKey];
              return (
                <ProfileScoreCard
                  key={profileKey}
                  profileKey={profileKey}
                  score={entry?.score ?? 0}
                  footer={entry?.forecast || '...'}
                />
              );
            })}
          </div>

          <Card className="bg-white border-stone-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-800 flex items-center">
                <Star className="h-5 w-5 mr-2 text-yellow-400" />
                {t('insightsDailyHighlights')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {dailyHighlights.length > 0 ? dailyHighlights.map((highlight) => (
                  <article
                    key={highlight.id}
                    className="group rounded-2xl border border-yellow-100 bg-gradient-to-br from-white to-amber-50/60 p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 shadow-sm">
                        <div className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="mb-2 inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                          {t('profile_' + highlight.profileKey)}
                        </div>
                        <h4 className="text-sm font-semibold leading-6 text-slate-900">
                          {t('planet_' + highlight.aspect.natal)}{' '}
                          <span className="text-yellow-700">
                            {t('aspect_' + highlight.aspect.aspect)}
                          </span>{' '}
                          {t('planet_' + highlight.aspect.transit)}
                        </h4>

                        <p className="mt-2 text-sm leading-6 text-slate-600">
                          {highlight.aspect?.forecast || '...'}
                        </p>
                      </div>
                    </div>
                  </article>
                )) : (
                  <p className="text-sm text-slate-500">{topProfile?.transitEvaluation?.globalForecast || '...'}</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-800 flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2 text-orange-500" />
                {t('insightsDailyAttention')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                {dailyAttention.length > 0 ? dailyAttention.map((highlight) => (
                  <article
                    key={highlight.id}
                    className="rounded-2xl border border-orange-100 bg-white/90 p-4 shadow-sm"
                  >
                    <div className="mb-2 inline-flex rounded-full bg-orange-100 px-2.5 py-1 text-xs font-medium text-orange-800">
                      {t('profile_' + highlight.profileKey)}
                    </div>
                    <h4 className="text-sm font-semibold leading-6 text-slate-900">
                      {t('planet_' + highlight.aspect.natal)}{' '}
                      <span className="text-orange-700">
                        {t('aspect_' + highlight.aspect.aspect)}
                      </span>{' '}
                      {t('planet_' + highlight.aspect.transit)}
                    </h4>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      {highlight.aspect?.forecast || '...'}
                    </p>
                  </article>
                )) : (
                  <p className="text-sm text-slate-500">{t('dashboardNone')}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="weekly" className="space-y-6">
          <WeeklyStrip days={weekCalendarDays}>

          <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-800 flex items-center">
                <Calendar className="h-6 w-6 mr-2 text-indigo-600" />
                {t('insightsWeeklyTheme')} {weeklyHighlights?.positive?.profileKey ? t('profile_' + weeklyHighlights?.positive?.profileKey) : '...'}
              </CardTitle>
              <CardDescription className="text-slate-600">{weeklyHighlights?.positive?.globalForecast || '...'}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-indigo-900 mb-2">{t('insightsWeeklyHighlights')}</h4>
                  <div className="space-y-2">
                    {weeklyHighlights?.positive?.transitEvaluations && (
                      <div className="space-y-2">
                        {weeklyHighlights.positive.transitEvaluations.map((evaluation, evalIndex) => (
                          <div key={evalIndex} className="space-y-2">
                            {evaluation.aspectForecasts?.map((forecast, forecastIndex) => (
                              <div key={forecastIndex} className="flex items-center space-x-3 p-3 bg-indigo-100/50 rounded-lg">
                                <TrendingUp className="h-4 w-4 text-indigo-600" />
                                <span className="text-sm text-indigo-900">
                                  <strong>
                                    {t('planet_' + forecast.transit)} {t('aspect_' + forecast.aspect)} {t('planet_' + forecast.natal)}:
                                  </strong>
                                  &nbsp;{forecast.forecast}
                                </span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-800 flex items-center">
                <AlertTriangle className="h-6 w-6 mr-2 text-orange-600" />
                {t('insightsWeeklyImprovement')} {weeklyHighlights?.negative?.profileKey ? t('profile_' + weeklyHighlights?.negative?.profileKey) : '...'}
              </CardTitle>
              <CardDescription className="text-slate-600">{weeklyHighlights?.negative?.globalForecast || '...'}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-orange-900 mb-2">{t('insightsWeeklyAttention')}</h4>
                  <div className="space-y-2">
                    {weeklyHighlights?.negative?.transitEvaluations && (
                      <div className="space-y-2">
                        {weeklyHighlights.negative.transitEvaluations.map((evaluation, evalIndex) => (
                          <div key={evalIndex} className="space-y-2">
                            {evaluation.aspectForecasts?.map((forecast, forecastIndex) => (
                              <div key={forecastIndex} className="flex items-center space-x-3 p-3 bg-orange-100/50 rounded-lg">
                                <AlertTriangle className="h-4 w-4 text-orange-600" />
                                <span className="text-sm text-orange-900">
                                  <strong>
                                    {t('planet_' + forecast.transit)} {t('aspect_' + forecast.aspect)} {t('planet_' + forecast.natal)}:
                                  </strong>
                                  &nbsp;{forecast.forecast}
                                </span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          </WeeklyStrip>

        </TabsContent>

        <TabsContent value="monthly" className="space-y-6">
          <InsightsCalendar days={monthCalendarDays} startDate={monthCalendarStart}>
          <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-slate-800 flex items-center">
                <Moon className="h-6 w-6 mr-2 text-purple-600" />
                {t('insightsMonthlyTheme')} {monthlyHighlights?.positive?.profileKey ? t('profile_' + monthlyHighlights?.positive?.profileKey) : '...'}
              </CardTitle>
              <CardDescription className="text-slate-600">{monthlyHighlights?.positive?.globalForecast || '...'}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-purple-900 mb-3">{t('insightsMonthlyTransits')}</h4>
                  <div className="space-y-3">
                    {monthlyHighlights?.positive?.transitEvaluations && (
                      <div className="space-y-2">
                        {[...monthlyHighlights.positive.transitEvaluations]
                          .sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0))
                          .map((evaluation) => (
                            <div key={evaluation.date?.toISOString()} className="space-y-2">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold bg-purple-200 text-purple-900 px-2 py-1 rounded-full">
                                  dia {evaluation.date?.getDate()}
                                </span>
                                <span className="text-xs text-purple-700 font-medium">
                                  {evaluation.date?.toLocaleDateString(i18n.language, { weekday: 'long' })}
                                </span>
                              </div>

                              <div className="space-y-2">
                                {evaluation.aspectForecasts?.map((forecast, i) => (
                                  <div key={i} className="flex items-start gap-3 p-3 bg-white/60 border border-purple-100/50 rounded-lg shadow-sm">
                                    <Star className="h-4 w-4 text-yellow-500 mt-0.5 shrink-0" />
                                    <div>
                                      <p className="text-sm text-slate-800 font-medium">
                                        {t('planet_' + forecast.transit)} {t('aspect_' + forecast.aspect)} {t('planet_' + forecast.natal)}
                                      </p>
                                      <p className="text-xs text-slate-600 leading-relaxed">
                                        {forecast.forecast}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          </InsightsCalendar>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Insights;



